import React from 'react';
import { Box, Typography, Stack, Chip, Avatar, ButtonBase } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import StarIcon from '@mui/icons-material/Star';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import { Carwash } from '../../types/CarwashType';
import { formatDistance, formatAddress, isOpenNow } from '../../utils/carwashHelpers';

interface Props {
  carwash: Carwash;
  selected: boolean;
  onClick: (carwash: Carwash) => void;
}

function PartnerCard({ carwash, selected, onClick }: Props) {
  const openStatus =
    carwash.operating_hours && carwash.operating_hours.length > 0
      ? isOpenNow(carwash.operating_hours)
      : null;

  return (
    <ButtonBase
      onClick={() => onClick(carwash)}
      sx={{
        width: '100%',
        textAlign: 'left',
        display: 'block',
        borderRadius: 2,
        border: '2px solid',
        borderColor: selected ? '#00b4e4' : 'transparent',
        backgroundColor: selected ? '#e3f6fd' : '#fff',
        boxShadow: selected ? '0 2px 12px rgba(0,180,228,0.25)' : '0 1px 4px rgba(0,0,0,0.08)',
        transition: 'all 0.2s',
        '&:hover': { borderColor: '#00b4e4', boxShadow: '0 2px 12px rgba(0,180,228,0.2)' },
        mb: 1.5,
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ p: 1.5 }}>
        <Avatar
          src={carwash.logo || undefined}
          alt={carwash.car_wash_name}
          variant="rounded"
          sx={{ width: 56, height: 56, bgcolor: '#e3f6fd', flexShrink: 0 }}
        >
          <LocalCarWashIcon sx={{ color: '#00b4e4' }} />
        </Avatar>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          {/* Linha 1: Nome + classificação */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.25 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}
            >
              {carwash.car_wash_name}
            </Typography>
            {carwash.average_rating !== undefined && carwash.average_rating > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                <StarIcon sx={{ fontSize: 13, color: '#FFB400' }} />
                <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1 }}>
                  {carwash.average_rating.toFixed(1)}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Linha 2: Endereço */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.4, minWidth: 0 }}>
            <PlaceIcon sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {formatAddress(carwash)}
            </Typography>
          </Stack>

          {/* Linha 3: Status aberto/fechado + distância */}
          <Stack direction="row" alignItems="center" spacing={0.75}>
            {openStatus !== null && (
              <Chip
                label={openStatus ? 'Aberto agora' : 'Fechado'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: openStatus ? '#e8f5e9' : '#f0f0f0',
                  color: openStatus ? '#2e7d32' : '#757575',
                }}
              />
            )}
            {carwash.distance !== undefined && (
              <Chip
                label={formatDistance(carwash.distance)}
                size="small"
                sx={{ height: 18, fontSize: 10, bgcolor: '#e3f6fd', color: '#00b4e4', fontWeight: 700 }}
              />
            )}
          </Stack>
        </Box>
      </Stack>
    </ButtonBase>
  );
}

export default PartnerCard;
